import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  FolderOpen,
  ShoppingCart,
  ClipboardList,
  Truck,
  FileText,
  Boxes,
  Users,
  LogOut,
  Menu,
  X,
  FlaskConical,
  ChevronRight,
  Bell,
  User
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { useAuth } from '../../context/AuthContext';

const PortalLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin, isSales, isWarehouse } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const navItems = [
    { 
      path: '/portal', 
      icon: LayoutDashboard, 
      label: 'Dashboard',
      roles: ['admin', 'sales', 'customer', 'warehouse']
    },
    { 
      path: '/portal/products', 
      icon: Package, 
      label: 'Products',
      roles: ['admin', 'sales', 'customer', 'warehouse']
    },
    { 
      path: '/portal/categories', 
      icon: FolderOpen, 
      label: 'Categories',
      roles: ['admin']
    },
    { 
      path: '/portal/sales-orders', 
      icon: ShoppingCart, 
      label: 'Sales Orders',
      roles: ['admin', 'sales', 'customer', 'warehouse']
    },
    { 
      path: '/portal/purchase-orders', 
      icon: ClipboardList, 
      label: 'Purchase Orders',
      roles: ['admin', 'sales']
    },
    { 
      path: '/portal/delivery', 
      icon: Truck, 
      label: 'Delivery Orders',
      roles: ['admin', 'sales', 'warehouse']
    },
    { 
      path: '/portal/invoices', 
      icon: FileText, 
      label: 'Invoices',
      roles: ['admin', 'sales', 'customer']
    },
    { 
      path: '/portal/inventory', 
      icon: Boxes, 
      label: 'Inventory',
      roles: ['admin', 'sales', 'warehouse']
    },
    { 
      path: '/portal/customers', 
      icon: Users, 
      label: 'Customers',
      roles: ['admin', 'sales']
    },
  ];

  const filteredNavItems = navItems.filter(item => 
    item.roles.includes(user?.role)
  );

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'bg-purple-100 text-purple-700',
      sales: 'bg-blue-100 text-blue-700',
      customer: 'bg-green-100 text-green-700',
      warehouse: 'bg-amber-100 text-amber-700',
    };
    return colors[role] || 'bg-slate-100 text-slate-700';
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="portal-layout">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`portal-sidebar ${sidebarOpen ? 'open' : ''}`}
        data-testid="portal-sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between p-4 border-b border-slate-800">
            <Link to="/portal" className="flex items-center gap-2">
              <FlaskConical className="w-7 h-7 text-orange-500" />
              <span className="text-lg font-bold text-white">ChemCore</span>
            </Link>
            <button
              className="lg:hidden text-slate-400 hover:text-white"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {filteredNavItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`nav-link ${isActive ? 'active' : ''}`}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-slate-800">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-slate-300" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                <Badge className={`text-xs ${getRoleBadgeColor(user?.role)}`}>
                  {user?.role}
                </Badge>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-800"
              onClick={handleLogout}
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className={`portal-main ${sidebarOpen ? '' : ''}`}>
        {/* Top bar */}
        <header className="sticky top-0 z-20 bg-white border-b border-slate-200">
          <div className="flex items-center justify-between px-4 sm:px-6 lg:px-8 h-16">
            <div className="flex items-center gap-4">
              <button
                className="lg:hidden p-2 text-slate-600 hover:text-slate-900"
                onClick={() => setSidebarOpen(true)}
                data-testid="mobile-menu-toggle"
              >
                <Menu className="w-5 h-5" />
              </button>
              
              {/* Breadcrumb */}
              <div className="hidden sm:flex items-center text-sm text-slate-500">
                <Link to="/portal" className="hover:text-slate-700">Portal</Link>
                {location.pathname !== '/portal' && (
                  <>
                    <ChevronRight className="w-4 h-4 mx-2" />
                    <span className="text-slate-900 font-medium">
                      {filteredNavItems.find(item => item.path === location.pathname)?.label || 'Page'}
                    </span>
                  </>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Notifications */}
              <Button variant="ghost" size="icon" className="relative">
                <Bell className="w-5 h-5 text-slate-600" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-orange-500 rounded-full"></span>
              </Button>

              {/* User dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-2" data-testid="user-menu-button">
                    <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-slate-600" />
                    </div>
                    <span className="hidden sm:block text-sm font-medium text-slate-700">
                      {user?.name}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col">
                      <span>{user?.name}</span>
                      <span className="text-xs font-normal text-slate-500">{user?.email}</span>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content */}
        <div className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default PortalLayout;
